import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Mail, ArrowLeft, Save, Eye, Code, FileText, RefreshCw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { cn } from '@/lib/utils';

// Interface para os dados de preview
interface PreviewData {
  name: string;
  email: string;
  company: string;
  date: string;
  link: string;
  code: string;
  product: string;
  amount: string;
  invoice_number: string;
  [key: string]: string;
}

// Schema de validação para o formulário
const templateSchema = z.object({
  name: z.string().min(3, { message: 'O nome deve ter pelo menos 3 caracteres' }),
  subject: z.string().min(3, { message: 'O assunto deve ter pelo menos 3 caracteres' }),
  body: z.string().min(10, { message: 'O corpo do email deve ter pelo menos 10 caracteres' }),
  is_active: z.boolean().default(true)
});

type TemplateFormValues = z.infer<typeof templateSchema>;

const EmailTemplateCreate = () => {
  const navigate = useNavigate();
  const [editorMode, setEditorMode] = useState<'simple' | 'html'>('simple');
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Monitorar mudanças no editorMode
  useEffect(() => {
    console.log('Editor mode alterado para:', editorMode);
  }, [editorMode]);

  const [previewData] = useState<PreviewData>({
    name: 'João Silva',
    email: 'joao.silva@exemplo.com',
    company: 'Empresa ABC Ltda',
    date: new Date().toLocaleDateString('pt-BR'),
    link: 'https://exemplo.com/confirmar',
    code: '123456',
    product: 'Plano Premium',
    amount: 'R$ 99,90',
    invoice_number: 'INV-2023-001'
  });

  // Formulário para adicionar template
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      subject: '',
      body: '',
      is_active: true
    }
  });

  // Gerar preview do template
  const generatePreview = () => {
    try {
      console.log('Gerando preview. Modo atual:', editorMode);
      const bodyContent = form.getValues('body');
      console.log('Conteúdo do corpo:', bodyContent);

      let previewContent = bodyContent;
      
      // Substituir variáveis pelos valores de exemplo
      Object.keys(previewData).forEach(key => {
        const regex = new RegExp(`{${key}}`, 'g');
        previewContent = previewContent.replace(regex, previewData[key]);
      });

      // Adicionar estilos básicos para o preview
      const styledHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.6; 
              color: #333; 
              margin: 0;
              padding: 0;
            }
            h1, h2, h3 { 
              color: #2563eb; 
              margin-top: 0;
              margin-bottom: 20px;
            }
            p {
              margin-bottom: 16px;
              margin-top: 0;
            }
            a { color: #2563eb; text-decoration: none; font-weight: 500; }
            a:hover { text-decoration: underline; }
            .footer { 
              margin-top: 30px; 
              font-size: 12px; 
              color: #666; 
              border-top: 1px solid #eee; 
              padding-top: 15px; 
            }
            img { max-width: 100%; height: auto; }
            .button {
              display: inline-block;
              background-color: #2563eb;
              color: white;
              padding: 10px 20px;
              border-radius: 4px;
              text-decoration: none;
              font-weight: bold;
              margin: 10px 0;
            }
            .button:hover {
              background-color: #1d4ed8;
              text-decoration: none;
            }
          </style>
        </head>
        <body>
          ${previewContent}
        </body>
        </html>
      `;
      
      console.log('HTML gerado para preview:', styledHtml);
      setPreviewHtml(styledHtml);
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      toast.error('Erro ao gerar preview do template');
    }
  };

  // Atualizar preview quando o conteúdo do corpo mudar
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'body') {
        generatePreview();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Gerar preview inicial
  useEffect(() => {
    generatePreview();
  }, []);

  // Salvar template
  const handleSaveTemplate = async (values: TemplateFormValues) => {
    setSubmitting(true);
    try {
      // Inserir o template principal
      const { data, error } = await supabase
        .from('email_templates')
        .insert([values])
        .select();

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Criar a primeira versão do template
        const templateId = data[0].id;
        const versionData = {
          email_template_id: templateId,
          version_number: 1,
          subject: values.subject,
          html_content: values.body,
          plain_text_content: null, // Opcional
          active: true
        };
        
        const { error: versionError } = await supabase
          .from('email_template_versions')
          .insert([versionData]);
          
        if (versionError) throw versionError;
      }
      
      toast.success('Template de email criado com sucesso');
      navigate('/transactional-emails');
    } catch (error: any) {
      console.error('Erro ao criar template:', error);
      toast.error('Erro ao criar template de email', {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="icon" 
            onClick={() => navigate('/transactional-emails')}
            className="mr-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold">Criar Template de Email</h1>
            <p className="text-muted-foreground">
              Crie um novo template para emails transacionais
            </p>
          </div>
        </div>
        <Button 
          onClick={form.handleSubmit(handleSaveTemplate)} 
          disabled={submitting}
          className="gap-1"
        >
          <Save className="h-4 w-4" /> 
          {submitting ? 'Salvando...' : 'Salvar Template'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Informações do Template</CardTitle>
            <CardDescription>
              Preencha os campos abaixo para criar um novo template de email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSaveTemplate)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Template</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Boas-vindas, Confirmação de Pagamento, etc." {...field} />
                      </FormControl>
                      <FormDescription>
                        Um nome descritivo para identificar o template.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assunto do Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Bem-vindo à nossa plataforma!" {...field} />
                      </FormControl>
                      <FormDescription>
                        O assunto que aparecerá no email. Você pode usar variáveis como {'{'}nome{'}'}.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Separator className="my-4" />
                
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-extrabold">Corpo do Email</h3>
                  <div className="flex items-center space-x-2">
                    <Button 
                      variant={editorMode === 'simple' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('Alterando para modo simple');
                        setEditorMode('simple');
                      }}
                      className="gap-1"
                      type="button"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      Texto Simples
                    </Button>
                    <Button 
                      variant={editorMode === 'html' ? 'default' : 'outline'} 
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('Alterando para modo html');
                        setEditorMode('html');
                      }}
                      className="gap-1"
                      type="button"
                    >
                      <Code className="h-3.5 w-3.5" />
                      HTML
                    </Button>
                  </div>
                </div>
                
                <FormField
                  control={form.control}
                  name="body"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea 
                          placeholder={editorMode === 'simple' 
                            ? "Conteúdo do email. Você pode usar variáveis como {nome} que serão substituídas pelos valores reais." 
                            : "<h1>Olá {name},</h1>\n<p>Bem-vindo à nossa plataforma!</p>\n<p>Clique <a href='{link}'>aqui</a> para confirmar seu cadastro.</p>"
                          }
                          className={cn(
                            "min-h-[300px]",
                            editorMode === 'html' ? "font-mono text-xs" : ""
                          )}
                          {...field} 
                          onFocus={() => {
                            console.log('Editor focado. Modo atual:', editorMode);
                            console.log('Classes aplicadas:', 
                              cn("min-h-[300px]", editorMode === 'html' ? "font-mono text-xs" : "")
                            );
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        {editorMode === 'simple' 
                          ? "O conteúdo do email. Suporta HTML e variáveis entre chaves simples como {nome}."
                          : "Insira o código HTML do email. Você pode usar variáveis entre chaves simples como {nome}."
                        }
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Ativo</FormLabel>
                        <FormDescription>
                          Determina se este template pode ser usado para enviar emails
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Preview do Template</CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  console.log('Atualizando preview manualmente');
                  generatePreview();
                }}
                className="gap-1"
                type="button"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Atualizar Preview
              </Button>
            </div>
            <CardDescription>
              Visualização do email com dados de exemplo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="preview" className="mt-4">
              <TabsList className="mb-2">
                <TabsTrigger value="preview">Preview</TabsTrigger>
                <TabsTrigger value="variables">Variáveis Disponíveis</TabsTrigger>
              </TabsList>
              
              {/* Preview */}
              <TabsContent value="preview">
                <iframe 
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-[600px] border-0 mt-2"
                  sandbox="allow-same-origin allow-scripts"
                />
              </TabsContent>
              
              {/* Variáveis Disponíveis */}
              <TabsContent value="variables" className="border rounded-md p-4 mt-2">
                <div className="mb-4">
                  <h3 className="text-lg font-extrabold mb-2">Variáveis Disponíveis</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Você pode usar as seguintes variáveis no seu template. Elas serão substituídas pelos valores reais quando o email for enviado.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div><code>{'{name}'}</code> - João Silva</div>
                    <div><code>{'{email}'}</code> - joao.silva@exemplo.com</div>
                    <div><code>{'{company}'}</code> - Empresa ABC Ltda</div>
                    <div><code>{'{date}'}</code> - {new Date().toLocaleDateString('pt-BR')}</div>
                    <div><code>{'{link}'}</code> - https://exemplo.com/confirmar</div>
                    <div><code>{'{code}'}</code> - 123456</div>
                    <div><code>{'{product}'}</code> - Plano Premium</div>
                    <div><code>{'{amount}'}</code> - R$ 99,90</div>
                    <div><code>{'{invoice_number}'}</code> - INV-2023-001</div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailTemplateCreate; 